import { Box } from '@eatclubapp/ec-component-library';
import React, { lazy, Suspense } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isEmpty } from '../utils/baseHelpers';
import { FEATURE_SCOPES, hasAccess } from '../utils/permissions';
import routes from './Routes';
import { getAppConfig } from '../constants/appConfig';
import { usePageViews } from '../utils/hooks/usePageViews';

// Routes
const NotFound = lazy(() => import('../components/notFound/NotFound'));

const SwitchRoutes = () => {
  usePageViews();

  const appConfig = getAppConfig();
  const user = useSelector((state) => state.user);
  const { restaurant } = useSelector((state) => state.restaurantActive);
  const { forecasterEnabled, analyticsEnabled, forecastLoading, analyticsLoading } = useSelector(
    (state) => state.forecasterEnabled,
  );

  const isPartnerLogin = user.userInfo?.userType === 'partner';

  // NOTE: Do not show Deals page if only platform is whitelabel
  const whitelabel = restaurant.platform === 'whitelabel';
  const { obeeEnabled, premium } = restaurant;

  const getDefaultRoute = () => {
    const searchParams = window.location.search;

    // Hack for a bug in react router where the route path for "/" doesn't match the actual pathname
    if (window.location.pathname !== '/' && !isEmpty(window.location.pathname)) {
      return window.location.pathname + searchParams;
    }

    if (obeeEnabled && hasAccess(user, FEATURE_SCOPES.BOOKINGS)) {
      if (premium) {
        return `/livebookings/floorplan${searchParams}`;
      }

      return `/livebookings/runsheet${searchParams}`;
    }

    if (hasAccess(user, FEATURE_SCOPES.INSIGHTS)) {
      return `/insights${searchParams}`;
    }

    // If we can't access the normal default pages, fall back to another page
    return `/transactions${searchParams}`;
  };

  return (
    <Switch>
      {/* Default route, not necessarily analytics */}
      <Route exact path={['/', '/index.html']}>
        <Suspense fallback={<Box />}>
          <Redirect to={getDefaultRoute()} />
        </Suspense>
      </Route>
      {routes
        .filter((route) => !appConfig.blockedRoutes.includes(route.path))
        .map((route) => {
          if (route.blockWhiteLabel && whitelabel) {
            return (
              <Route key={route.path} exact={route.exact} path={route.path}>
                <Suspense fallback={<Box />}>
                  <Redirect to='/insights' />
                </Suspense>
              </Route>
            );
          }

          if (route.blockPartners && isPartnerLogin) {
            return (
              <Route key={route.path} exact={route.exact} path={route.path}>
                <Suspense fallback={<Box />}>
                  <Redirect to='/insights' />
                </Suspense>
              </Route>
            );
          }

          // Only redirect once loading is complete and the flag is explicitly false.
          // Don't redirect while still loading (null) to avoid premature redirects.
          if (route.forecasterEnabledRequired && !forecastLoading && forecasterEnabled === false) {
            return (
              <Route key={route.path} exact={route.exact} path={route.path}>
                <Suspense fallback={<Box />}>
                  <Redirect to='/landing-forecast' />
                </Suspense>
              </Route>
            );
          }

          if (route.analyticsEnabledRequired && !analyticsLoading && analyticsEnabled === false) {
            return (
              <Route key={route.path} exact={route.exact} path={route.path}>
                <Suspense fallback={<Box />}>
                  <Redirect to='/landing-analytics' />
                </Suspense>
              </Route>
            );
          }

          if (route.obeeEnabledRequired && !obeeEnabled) {
            return (
              <Route key={route.path} exact={route.exact} path={route.path}>
                <Suspense fallback={<Box />}>
                  <Redirect to='/livebookings' />
                </Suspense>
              </Route>
            );
          }

          if (route.obeePremiumRequired && !premium) {
            return (
              <Route key={route.path} exact={route.exact} path={route.path}>
                <Suspense fallback={<Box />}>
                  <Redirect to='/livebookings' />
                </Suspense>
              </Route>
            );
          }

          // venues have no use for this page once on premium
          if (route.path === '/livebookings' && obeeEnabled && premium) {
            return (
              <Route key={route.path} exact={route.exact} path={route.path}>
                <Suspense fallback={<Box />}>
                  <Redirect to='/livebookings/runsheet' />
                </Suspense>
              </Route>
            );
          }

          return (
            <Route key={route.path} exact={route.exact} path={route.path}>
              <Suspense fallback={<Box />}>
                <route.main />
              </Suspense>
            </Route>
          );
        })}
      {/* Route doesnt exist in Routes */}
      <Route path='*'>
        <Suspense fallback={<Box />}>
          <NotFound />
        </Suspense>
      </Route>
    </Switch>
  );
};

export default SwitchRoutes;
